/**
 * usePatientUpcomingAppointments.js
 *
 * Próximos agendamentos ativos do paciente a partir do dia do novo agendamento.
 * Alimenta o aviso do drawer de criação ("já tem agendamento marcado"), para a
 * recepção reagendar o atendimento existente em vez de criar um segundo.
 *
 * Falha na consulta = lista vazia: o aviso é ajuda, nunca bloqueia a criação.
 */

import { useEffect, useState } from 'react';
import { GetPatientUpcomingAppointmentsAPI } from '../../../../api/API';
import { formatDateKey } from '../utils/recurrenceUtils';

// CallPOSTAPI não chama o callback em queda de rede / timeout (10 s):
// sem este limite o drawer ficaria esperando a consulta para sempre.
const GIVE_UP_MS = 12000;

/**
 * @param {string|number|null} patientId
 * @param {Date|null} date - dia do novo agendamento
 * @param {boolean} enabled
 * @returns {{ upcoming: Array, isLoading: boolean }}
 */
export default function usePatientUpcomingAppointments(patientId, date, enabled) {
  const [upcoming, setUpcoming] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fromDate = date ? formatDateKey(date) : '';

  useEffect(() => {
    setUpcoming([]);
    if (!enabled || patientId == null || patientId === '' || !fromDate) {
      setIsLoading(false);
      return undefined;
    }

    // Troca rápida de paciente: só a resposta da consulta mais recente vale
    let active = true;
    setIsLoading(true);

    const giveUp = setTimeout(() => {
      if (active) setIsLoading(false);
    }, GIVE_UP_MS);

    GetPatientUpcomingAppointmentsAPI(patientId, fromDate, (response) => {
      if (!active) return;
      clearTimeout(giveUp);
      setIsLoading(false);
      const list = response?.data?.appointments;
      setUpcoming(Array.isArray(list) ? list : []);
    });

    return () => {
      active = false;
      clearTimeout(giveUp);
    };
  }, [patientId, fromDate, enabled]);

  return { upcoming, isLoading };
}
